import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { endOfDay, isAfter, parseISO } from "date-fns";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { LojaPageContainer } from "layouts/pages/loja/common";
import { debouce } from "helpers/utils";

import ModalCupom from "./components/ModalCupom";
import TableCupomEvento from "./components/TableCupom";

import { deleteCupom, getAllCupom } from "services/cupons";
import { ICupomEvento } from "interfaces/cupom";
import { CUPONS_ACCENT, cuponsToolbarSx, saveButtonSx } from "./cupons.theme";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? CUPONS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function isCupomExpired(validade: string): boolean {
  const dataExpired = endOfDay(parseISO(validade.slice(0, 10)));
  return isAfter(new Date(), dataExpired);
}

export default function Cupons(): JSX.Element {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<ICupomEvento[] | null>(null);
  const [listCupom, setListCupom] = useState<ICupomEvento[]>([]);
  const [selected, setSelected] = useState<ICupomEvento | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const isFiltered = searchResults !== null;
  const displayedList = searchResults ?? listCupom;

  const stats = useMemo(() => {
    const ativos = listCupom.filter((c) => !isCupomExpired(c.validade)).length;
    const disponiveis = listCupom.reduce((acc, c) => acc + c.disponivel, 0);
    return { ativos, disponiveis };
  }, [listCupom]);

  const loadCupons = useCallback(async () => {
    const resp = await getAllCupom();
    setListCupom(resp.data);
  }, []);

  useEffect(() => {
    void loadCupons();
  }, [loadCupons]);

  useEffect(() => {
    setSearchResults(null);
    setSearch("");
  }, [listCupom.length]);

  const applySearch = useCallback(
    (term: string) => {
      const q = term.trim().toLowerCase();
      if (!q) {
        setSearchResults(null);
        return;
      }
      setSearchResults(
        listCupom.filter(
          (item) =>
            item.evento?.titulo?.toLowerCase().includes(q) ||
            item.codigoPromocional?.toLowerCase().includes(q),
        ),
      );
    },
    [listCupom],
  );

  const debouncedSearch = useMemo(
    () => debouce(applySearch, 300),
    [applySearch],
  );

  const handleDeleteCupom = async (id: number) => {
    setListCupom((prev) => prev.filter((item) => item.id !== id));
    await deleteCupom(id);
  };

  const handleEditCupom = (cupom: ICupomEvento) => {
    setSelected(cupom);
    setOpenModal(true);
  };

  const updateCupom = (type: "edit" | "create", cupom: ICupomEvento) => {
    if (type === "create") {
      setListCupom((prev) => [cupom, ...prev]);
    } else {
      void loadCupons();
    }
  };

  const handleOpenModal = () => {
    setSelected(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelected(null);
  };

  return (
    <LojaPageContainer
      title="Cupons"
      subtitle="Cupons de desconto vinculados aos eventos esportivos."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={handleOpenModal}
          sx={saveButtonSx}
        >
          Novo cupom
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "Resultados da busca" : "Total emitidos"}
            value={isFiltered ? displayedList.length : listCupom.length}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Exibindo agora" value={displayedList.length} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Cupons ativos" value={stats.ativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Usos disponíveis" value={stats.disponiveis} />
        </Grid>
      </Grid>

      <MDBox sx={cuponsToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ConfirmationNumberOutlinedIcon sx={{ color: CUPONS_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isFiltered
                ? `${displayedList.length} cupom${displayedList.length === 1 ? "" : "s"} no filtro`
                : `${listCupom.length} cupom${listCupom.length === 1 ? "" : "s"} emitido${listCupom.length === 1 ? "" : "s"}`}
            </MDTypography>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel htmlFor="cupons-search">Pesquisar cupom</InputLabel>
            <OutlinedInput
              id="cupons-search"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSearch(e.target.value);
              }}
              label="Pesquisar cupom"
              placeholder="Código ou evento…"
              endAdornment={
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>
        </Stack>
      </MDBox>

      {displayedList.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum cupom encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe o campo para ver todos."
              : "Crie o primeiro cupom promocional para um evento."}
          </MDTypography>
        </MDBox>
      ) : (
        <TableCupomEvento
          cupom={displayedList}
          onEdit={handleEditCupom}
          onDelete={handleDeleteCupom}
        />
      )}

      <ModalCupom
        open={openModal}
        onClose={handleCloseModal}
        data={selected}
        updateListCupom={updateCupom}
      />
    </LojaPageContainer>
  );
}
